import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import { rolesCanAccess, type RouteAccessRule } from '../lib/route-permissions.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';

type RouteGuardProps = {
  /** Access rule from `ROUTE_ACCESS_RULES` (resource:action prefixes). */
  rule: RouteAccessRule;
  children: ReactNode;
};

/**
 * Per-route role gate for tenant operational surfaces (issue #131).
 *
 * Sits *inside* `ProtectedRoute` (auth is already guaranteed), so this
 * only answers "is this authenticated role allowed on this surface?".
 *
 * Behavior on deny:
 *  - Renders a friendly, brand-styled 403 surface IN PLACE — no
 *    redirect, so a deep-linked user gets an explanation instead of a
 *    silent bounce or a white screen, and there's no redirect loop.
 *  - The AppShell chrome (sidebar/topbar) still renders around it
 *    because RouteGuard is the route element, nested under the
 *    AppShell `<Outlet/>` — the user keeps their (permitted) nav.
 *
 * Why a 403 (not the 404-shape `PlatformAdminRoute` uses): platform
 * routes hide their existence to prevent enumeration of the operator
 * console. Tenant operational routes are not secret — every staffer
 * knows "Reports" exists; they just may not have access. A clear
 * "you don't have access" is the better UX and matches the server,
 * which returns 403 (not 404) for these.
 *
 * This is a UX gate. The server's `requirePermission` (RBAC-01 /
 * #130 / #156) is the authoritative boundary at the precise scope.
 */
export function RouteGuard({ rule, children }: RouteGuardProps): JSX.Element {
  const { staff } = useAuth();

  // #428 — the gate passes when ANY effective role (primary + additive
  // grants) can reach the surface; single-role sessions behave exactly
  // as before (effectiveRolesOf falls back to [role]).
  if (rolesCanAccess(effectiveRolesOf(staff), rule)) {
    return <>{children}</>;
  }

  return (
    <section
      className="ferro-admin-page"
      role="alert"
      aria-live="polite"
      data-testid="route-forbidden"
    >
      <h1 className="ferro-section__title">Access restricted</h1>
      <p className="ferro-muted">
        Your role doesn&apos;t have access to this page. If you think this is
        a mistake, ask an owner or manager to update your permissions.
      </p>
    </section>
  );
}
